import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Check, Info, Plus, RefreshCw, Search, Trash2, Zap } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import sentinelNetwork from '@/lib/api/sentinelNetwork';

export interface ProjectEntry {
  key: string;
  name: string;
  lastAnalysisDate?: string | null;
  manual?: boolean;
}

interface InlineProjectListProps {
  appId: string;
  platform: 'SonarQube' | 'BlackDuck';
  savedMappings: Array<{ repoName: string; projectKey: string }>;
  onSave: (mappings: Array<{ repoName: string; projectKey: string }>) => Promise<void>;
  disabled?: boolean;
  /** @deprecated No longer used — projects are never auto-fetched on mount. */
  autoFetch?: boolean;
  /** Git repos from data sources — used to show auto-mapping status on BlackDuck projects. */
  gitRepos?: Array<{ name: string; branch: string }>;
}

function formatDate(dateStr?: string | null) {
  if (!dateStr) return 'never scanned';
  try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
}

/**
 * Fuzzy-match a BD project segment against configured git repos.
 * Priority: exact → normalised → containment → best token overlap ≥ 50%.
 * Mirrors the same algorithm in blackDuckMetadataService.js.
 */
function findBestGitRepoMatch(
  bdSegment: string,
  gitRepos: Array<{ name: string; branch: string }>,
): { name: string; branch: string } | null {
  if (!gitRepos || gitRepos.length === 0 || !bdSegment) return null;

  const norm = (s: string) => s.toLowerCase().replace(/[-_\s]/g, '');
  const tok  = (s: string) => s.toLowerCase().split(/[-_\s]+/).filter(Boolean);

  const bdNorm   = norm(bdSegment);
  const bdTokens = tok(bdSegment);

  // 1. Exact case-insensitive
  let hit = gitRepos.find(r => r.name.toLowerCase() === bdSegment.toLowerCase());
  if (hit) return hit;

  // 2. Normalised exact
  hit = gitRepos.find(r => norm(r.name) === bdNorm);
  if (hit) return hit;

  // 3. Containment (normalised)
  hit = gitRepos.find(r => {
    const rn = norm(r.name);
    return rn.includes(bdNorm) || bdNorm.includes(rn);
  });
  if (hit) return hit;

  // 4. Best token overlap ≥ 50 %
  let bestScore = 0;
  let bestRepo: { name: string; branch: string } | null = null;
  for (const r of gitRepos) {
    const rt     = tok(r.name);
    const shared = bdTokens.filter(t => rt.includes(t)).length;
    const score  = shared / Math.max(bdTokens.length, rt.length, 1);
    if (score > bestScore && score >= 0.5) {
      bestScore = score;
      bestRepo  = r;
    }
  }
  return bestRepo;
}

export default function InlineProjectList({
  appId,
  platform,
  savedMappings,
  onSave,
  disabled = false,
  autoFetch = false,
  gitRepos,
}: InlineProjectListProps) {
  const theme = useTheme();

  // Confirmed projects in the main list
  const [confirmed, setConfirmed] = useState<ProjectEntry[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  // Per-project repo assignment: projectKey → gitRepoName ('' = global/unassigned)
  const [repoAssignments, setRepoAssignments] = useState<Record<string, string>>({});

  const [loadingAll, setLoadingAll] = useState(false);
  const [fetchError, setFetchError] = useState('');
  // True once saved mappings have been loaded or the user has explicitly fetched.
  const [hasFetchedAll, setHasFetchedAll] = useState(false);

  // Input field (shared for Discover and Add)
  const [query, setQuery] = useState('');

  // Discover flow
  const [discovering, setDiscovering] = useState(false);
  const [discoverResults, setDiscoverResults] = useState<ProjectEntry[]>([]);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discoverSelected, setDiscoverSelected] = useState<Set<string>>(new Set());
  const [discoverError, setDiscoverError] = useState('');

  const labelText = platform === 'SonarQube' ? 'SonarQube' : 'BlackDuck';
  const endpoint = platform === 'SonarQube' ? '/tools/sonar/projects' : '/tools/blackduck/projects';

  // On mount: restore previously saved mappings if any.
  // Projects are never auto-fetched on mount — the user must click Discover or
  // the Refresh icon to load the full project list from the API.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    if (savedMappings.length > 0) {
      setHasFetchedAll(true);
      setSelected(new Set(savedMappings.map((m) => m.projectKey)));
      setConfirmed(
        savedMappings.map((m): ProjectEntry => ({
          key: m.projectKey,
          name: m.repoName || m.projectKey,
          manual: true,
        })),
      );
      // Restore per-repo assignments from saved mappings
      const assignments: Record<string, string> = {};
      for (const m of savedMappings) {
        if (m.repoName) assignments[m.projectKey] = m.repoName;
      }
      if (Object.keys(assignments).length > 0) setRepoAssignments(assignments);
    }
    // No auto-fetch: list starts empty when there are no saved mappings.
    // Use the Refresh icon to load all projects, or Discover to search by prefix.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Load ALL projects from API (triggered by the Refresh icon) — auto-selects all */
  const fetchAll = useCallback(async () => {
    setLoadingAll(true);
    setFetchError('');
    try {
      const res = await sentinelNetwork.get(endpoint, { params: { appId } });
      const fetched: ProjectEntry[] = (res.data.projects || []).map((p: any) => ({ ...p, manual: false }));
      setConfirmed(fetched);
      setHasFetchedAll(true);
      setSelected(new Set(fetched.map((p) => p.key)));
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed',
      );
      setHasFetchedAll(true);
    } finally {
      setLoadingAll(false);
    }
  }, [appId, endpoint]);

  // Projects are only loaded on explicit user action (Discover button or the Refresh icon).
  // No auto-fetch on mount — the list starts empty when there are no saved mappings.

  /**
   * Discover — searches the SonarCloud/BlackDuck API using the typed prefix.
   * When the query is empty, all projects for the token are returned.
   * Shows results for the user to pick from. Never adds silently.
   */
  const discover = async () => {
    const q = query.trim();
    // Empty query is allowed — returns all projects from the API
    setDiscovering(true);
    setDiscoverError('');
    setDiscoverResults([]);
    setDiscoverOpen(false);
    try {
      const params: Record<string, string> = { appId };
      if (q) params.q = q;
      const res = await sentinelNetwork.get(endpoint, { params });
      const rawProjects = Array.isArray(res.data?.projects) ? res.data.projects
        : Array.isArray(res.data?.data?.projects) ? res.data.data.projects
        : [];
      const results: ProjectEntry[] = rawProjects.map((p: any) => ({ ...p, manual: false }));
      setDiscoverResults(results);
      setDiscoverSelected(new Set(results.map((p) => p.key))); // pre-select all
      setDiscoverOpen(true);
      if (results.length === 0) {
        setDiscoverError(
          q
            ? `No ${labelText} projects found matching "${q}". Use Add to add the key directly.`
            : `No ${labelText} projects found. Check your token or use Add to add keys directly.`
        );
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Unknown error';
      setDiscoverError(`${labelText} API error: ${msg}. Use Add to add the key directly.`);
      setDiscoverOpen(true);
    } finally {
      setDiscovering(false);
    }
  };

  /**
   * Add — directly adds one or more typed keys to the list without API lookup.
   * Supports comma/space-separated input for multiple keys.
   */
  const addDirect = () => {
    const raw = query.trim();
    if (!raw) return;
    const keys = raw.split(/[\s,]+/).filter(Boolean);
    const existing = new Set(confirmed.map((p) => p.key));
    const toAdd = keys
      .filter((k) => !existing.has(k))
      .map((k): ProjectEntry => ({ key: k, name: k, manual: true }));
    if (toAdd.length > 0) {
      setConfirmed((prev) => [...prev, ...toAdd]);
      setSelected((prev) => { const n = new Set(prev); toAdd.forEach((p) => n.add(p.key)); return n; });
    }
    setQuery('');
    setDiscoverOpen(false);
    setDiscoverError('');
  };

  /** Confirm selected discover results → move to main list */
  const confirmDiscovered = () => {
    const existing = new Set(confirmed.map((p) => p.key));
    const toAdd = discoverResults.filter((p) => discoverSelected.has(p.key) && !existing.has(p.key));
    if (toAdd.length > 0) {
      setConfirmed((prev) => [...prev, ...toAdd]);
      setSelected((prev) => { const n = new Set(prev); toAdd.forEach((p) => n.add(p.key)); return n; });
      // Auto-suggest repo assignment for newly discovered projects using fuzzy match
      if (gitRepos && gitRepos.length > 1) {
        const suggested: Record<string, string> = {};
        for (const proj of toAdd) {
          const segment = proj.key.includes('/') ? proj.key.split('/').pop()! : proj.key;
          const best = findBestGitRepoMatch(proj.name || segment, gitRepos);
          if (best) suggested[proj.key] = best.name;
        }
        if (Object.keys(suggested).length > 0) {
          setRepoAssignments(prev => ({ ...prev, ...suggested }));
        }
      }
    }
    setQuery('');
    setDiscoverOpen(false);
    setDiscoverResults([]);
    setDiscoverSelected(new Set());
    setDiscoverError('');
  };

  const cancelDiscover = () => {
    setDiscoverOpen(false);
    setDiscoverResults([]);
    setDiscoverSelected(new Set());
    setDiscoverError('');
  };

  const removeConfirmed = (key: string) => {
    setConfirmed((prev) => prev.filter((p) => p.key !== key));
    setSelected((prev) => { const n = new Set(prev); n.delete(key); return n; });
  };

  const toggleConfirmed = (key: string) => {
    if (disabled) return;
    setSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const toggleDiscover = (key: string) => {
    setDiscoverSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // When multiple git repos are configured, use the user-assigned repoName so
      // each project maps to exactly one repo.  An empty repoName keeps the legacy
      // "global" behaviour for single-repo projects or where no assignment is made.
      const mappings = [...selected].map((key) => ({
        repoName: repoAssignments[key] || '',
        projectKey: key,
      }));
      await onSave(mappings);
    } finally {
      setSaving(false);
    }
  };

  const savedKeys = savedMappings.map((m) => m.projectKey).sort().join(',');
  const currentKeys = [...selected].sort().join(',');
  // Also treat repo-assignment changes as unsaved
  const savedAssignments = Object.fromEntries(savedMappings.map(m => [m.projectKey, m.repoName || '']));
  const assignmentsChanged = [...selected].some(k => (repoAssignments[k] || '') !== (savedAssignments[k] || ''));
  const hasUnsavedChanges = savedKeys !== currentKeys || assignmentsChanged;

  return (
    <Box sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        px: 1.5, py: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderBottom: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '.06em' }}>
          {labelText} Projects
          {!loadingAll && confirmed.length > 0 && ` · ${confirmed.length} loaded`}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Select All / Deselect All for the confirmed list */}
          {confirmed.length > 0 && !disabled && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                const allSelected = selected.size === confirmed.length;
                setSelected(allSelected ? new Set() : new Set(confirmed.map((p) => p.key)));
              }}
              sx={{ textTransform: 'none', fontSize: 10, py: 0, px: 0.75, minWidth: 0, lineHeight: 1.6 }}
            >
              {selected.size === confirmed.length ? 'Deselect All' : 'Select All'}
            </Button>
          )}
          {selected.size > 0 && (
            <Chip label={`${selected.size} selected`} size="small" color="primary" sx={{ fontSize: 10, height: 20 }} />
          )}
          <Tooltip title={`Reload all ${labelText} projects from API`} arrow>
            <span>
              <Button size="small" onClick={fetchAll} disabled={loadingAll || disabled} sx={{ minWidth: 0, p: 0.5 }}>
                <RefreshCw size={13} />
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Input row */}
      <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            fullWidth
            placeholder={`Optional prefix — Discover loads all ${labelText} projects (or filters by prefix), Add saves a key directly`}
            value={query}
            disabled={disabled || discovering}
            onChange={(e) => { setQuery(e.target.value); cancelDiscover(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); discover(); } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment>,
              sx: { fontSize: 12 },
            }}
          />
          <Tooltip title={query.trim() ? `Search ${labelText} for projects starting with "${query.trim()}"` : `Load all ${labelText} projects`} arrow>
            <span>
              <Button
                size="small" variant="contained" onClick={discover}
                disabled={disabled || discovering}
                startIcon={discovering ? <CircularProgress size={12} color="inherit" /> : <Zap size={13} />}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: 12, minWidth: 100 }}
              >
                {discovering ? 'Searching…' : 'Discover'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Add key(s) directly — supports comma-separated for multiple" arrow>
            <span>
              <Button
                size="small" variant="outlined" onClick={addDirect}
                disabled={disabled || !query.trim()}
                startIcon={<Plus size={13} />}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: 12 }}
              >
                Add
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {/* Auto-load error */}
        {fetchError && confirmed.length === 0 && !discoverOpen && (
          <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: 11 }}>
            Could not auto-load {labelText} projects. Use <strong>Discover</strong> to search by prefix or <strong>Add</strong> to enter keys directly.
          </Alert>
        )}

        {/* Discover results / error panel */}
        <Collapse in={discoverOpen} unmountOnExit>
          <Box sx={{ mt: 1.5, border: '1px solid', borderColor: 'primary.light', borderRadius: 1, overflow: 'hidden' }}>
            {/* Panel header */}
            <Box sx={{
              px: 1.25, py: 0.75,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              borderBottom: '1px solid', borderColor: 'primary.light',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
            }}>
              <Typography variant="caption" fontWeight={700} color="primary.main"
                sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '.05em', flexShrink: 0 }}>
                Discover Results
                {discoverResults.length > 0 && ` · ${discoverResults.length} found`}
              </Typography>
              {discoverResults.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {/* Select All / Deselect All */}
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      const allSelected = discoverSelected.size === discoverResults.length;
                      setDiscoverSelected(
                        allSelected ? new Set() : new Set(discoverResults.map((p) => p.key))
                      );
                    }}
                    sx={{ textTransform: 'none', fontSize: 10, py: 0, px: 0.75, minWidth: 0, lineHeight: 1.6 }}
                  >
                    {discoverSelected.size === discoverResults.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Chip label={`${discoverSelected.size} to add`} size="small" color="primary"
                    variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                </Box>
              )}
            </Box>

            {/* Error */}
            {discoverError && (
              <Alert severity="warning" sx={{ m: 1.25, py: 0.5, fontSize: 11 }}>
                {discoverError}
              </Alert>
            )}

            {/* Results list */}
            {discoverResults.length > 0 && (
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {discoverResults.map((proj) => {
                  const checked = discoverSelected.has(proj.key);
                  return (
                    <Box key={proj.key} onClick={() => toggleDiscover(proj.key)} sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1.25, py: 0.75,
                      borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.5),
                      cursor: 'pointer',
                      bgcolor: checked ? alpha(theme.palette.primary.main, 0.07) : 'transparent',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      '&:last-child': { borderBottom: 'none' },
                      transition: 'background-color 0.1s',
                    }}>
                      <Checkbox size="small" checked={checked} sx={{ p: 0 }}
                        onClick={(e) => e.stopPropagation()} onChange={() => toggleDiscover(proj.key)} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontSize: 12, fontWeight: checked ? 600 : 400, color: 'text.primary' }}>
                          {proj.name || proj.key}
                        </Typography>
                        {proj.name && proj.name !== proj.key && (
                          <Typography variant="caption" color="text.secondary"
                            sx={{ fontFamily: 'monospace', fontSize: 10, display: 'block' }} noWrap>
                            {proj.key}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{
                        fontSize: 10, whiteSpace: 'nowrap',
                        color: proj.lastAnalysisDate ? 'text.disabled' : 'error.main',
                      }}>
                        {proj.lastAnalysisDate ? formatDate(proj.lastAnalysisDate) : 'never scanned'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Panel footer */}
            <Box sx={{
              px: 1.25, py: 0.75,
              borderTop: '1px solid', borderColor: 'primary.light',
              display: 'flex', justifyContent: 'flex-end', gap: 1,
            }}>
              <Button size="small" onClick={cancelDiscover} sx={{ textTransform: 'none', fontSize: 12 }}>
                Cancel
              </Button>
              {discoverResults.length > 0 && (
                <Button size="small" variant="contained" onClick={confirmDiscovered}
                  disabled={discoverSelected.size === 0}
                  startIcon={<Check size={12} />}
                  sx={{ textTransform: 'none', fontSize: 12 }}>
                  Add {discoverSelected.size > 0 ? `${discoverSelected.size} ` : ''}Selected
                </Button>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Loading all */}
      {loadingAll && (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Loading {labelText} projects…</Typography>
        </Box>
      )}

      {/* Empty state — shown before any fetch (prompt) and after an empty fetch (no results) */}
      {!loadingAll && confirmed.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, fontSize: 12 }}>
          {hasFetchedAll
            ? <>No projects found. Use <strong>Discover</strong> to search {labelText} or <strong>Add</strong> to enter keys directly.</>
            : <>Use <strong>Discover</strong> to search {labelText} projects, or <strong>Add</strong> to enter a project key directly.</>
          }
        </Typography>
      )}

      {/* Confirmed list */}
      {!loadingAll && confirmed.length > 0 && (
        <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
          {confirmed.map((proj) => {
            const isChecked = selected.has(proj.key);
            return (
              <Box key={proj.key} onClick={() => toggleConfirmed(proj.key)} sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 0.75,
                borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.5),
                cursor: disabled ? 'default' : 'pointer',
                bgcolor: isChecked ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                '&:hover': disabled ? {} : { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                '&:last-child': { borderBottom: 'none' },
                transition: 'background-color 0.1s',
              }}>
                <Checkbox size="small" checked={isChecked} disabled={disabled} sx={{ p: 0 }}
                  onClick={(e) => e.stopPropagation()} onChange={() => toggleConfirmed(proj.key)} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {proj.manual ? (
                    <Typography variant="caption" color="text.secondary"
                      sx={{ fontFamily: 'monospace', fontSize: 11 }} noWrap>
                      {proj.key}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" noWrap sx={{ fontSize: 12, fontWeight: isChecked ? 600 : 400, color: 'text.primary' }}>
                        {proj.name || proj.key}
                      </Typography>
                      {proj.name && proj.name !== proj.key && (
                      <Typography variant="caption" color="text.secondary"
                        sx={{ fontFamily: 'monospace', fontSize: 10, display: 'block' }} noWrap>
                        {proj.key}
                      </Typography>
                      )}
                    </>
                  )}
                </Box>
                {!proj.manual && (
                  <Typography variant="caption" sx={{
                    fontSize: 10, whiteSpace: 'nowrap',
                    color: proj.lastAnalysisDate ? 'text.disabled' : 'error.main',
                  }}>
                    {proj.lastAnalysisDate ? formatDate(proj.lastAnalysisDate) : 'never scanned'}
                  </Typography>
                )}
                {/* Per-repo mapping selector — shown when multiple git repos are configured */}
                {gitRepos && gitRepos.length > 1 && !disabled && (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
                  >
                    <Typography sx={{ fontSize: 10, color: 'text.disabled', whiteSpace: 'nowrap' }}>→ Repo:</Typography>
                    <Select
                      size="small"
                      value={repoAssignments[proj.key] || ''}
                      onChange={(e) => {
                        e.stopPropagation();
                        setRepoAssignments(prev => ({ ...prev, [proj.key]: e.target.value as string }));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        fontSize: 10,
                        height: 22,
                        minWidth: 100,
                        maxWidth: 160,
                        '& .MuiSelect-select': { py: 0, px: 0.75, fontSize: 10 },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: 11 }}>
                        <em>All repos (global)</em>
                      </MenuItem>
                      {gitRepos.map(r => (
                        <MenuItem key={r.name} value={r.name} sx={{ fontSize: 11 }}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
                {/* Mapping-status icon — BlackDuck only */}
                {platform === 'BlackDuck' && (() => {
                  const bdSegment = proj.key.includes('/') ? proj.key.split('/').pop() : proj.key;
                  const gitRepo = findBestGitRepoMatch(bdSegment || '', gitRepos || []);
                  return (
                    <Tooltip
                      arrow
                      title={
                        <Box sx={{ p: 0.25 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                            BD: {proj.key}
                          </Typography>
                          {gitRepo ? (
                            <Typography variant="caption" sx={{ display: 'block', color: 'success.light' }}>
                              → Git: {gitRepo.name} ({gitRepo.branch})
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ display: 'block', color: 'warning.light' }}>
                              ⚠ No matching git repo — excluded from report
                            </Typography>
                          )}
                        </Box>
                      }
                    >
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', cursor: 'default' }}
                        onClick={(e) => e.stopPropagation()}>
                        <Info size={11} color={gitRepo ? theme.palette.primary.main : theme.palette.warning.main} />
                      </Box>
                    </Tooltip>
                  );
                })()}
                {!disabled && (
                  <Tooltip title="Remove" arrow>
                    <IconButton size="small"
                      onClick={(e) => { e.stopPropagation(); removeConfirmed(proj.key); }}
                      sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                      <Trash2 size={12} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Divider />

      {/* Footer */}
      <Box sx={{
        px: 1.5, py: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: alpha(theme.palette.background.default, 0.4),
      }}>
        <Typography variant="caption" color="text.secondary">
          <strong>{selected.size}</strong> project{selected.size !== 1 ? 's' : ''} selected
        </Typography>
        <Tooltip
          title={selected.size === 0 ? 'Select at least one project' : !hasUnsavedChanges ? 'No changes to save' : ''}
          arrow
        >
          <span>
            <Button size="small" variant="contained" onClick={handleSave}
              disabled={disabled || saving || selected.size === 0 || !hasUnsavedChanges}
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <Check size={12} />}
              sx={{ textTransform: 'none', fontSize: 12 }}>
              {saving ? 'Saving…' : 'Save Selection'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
